// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * Telling a scanned module code from a part serial or a robot SN (component design §6,
 * §9.2), and whether a looked-up module may go into a slot.
 *
 * A port of `app/domains/archive/module_sn.py`: a module SN is 14 characters with the
 * module type letter at position 4 (`220L2608210001`); a module archive number is
 * `<prefix>-<type>-<6 digits>` (`RBT-L-000001`), accepted for a missing label. A robot SN
 * is 18 characters and is never a module. The server checks again at filing; this only
 * decides which slot a scan goes to, and says why a module cannot go in while the operator
 * is still holding it.
 */

import type { ModuleLookup } from "@/api/types";
import { normalizeSerial } from "./serials";

const MODULE_SN = /^[1-9]\d{2}([A-Z])\d{10}$/;
const MODULE_ARCHIVE_NO = /^[A-Z0-9]+-([A-Z])-\d{6,}$/;
const ROBOT_SN = /^\d{3}[0-9A-Z]{2}[A-Z0-9]{3}\d{10}$/;

/** The module type a scanned code names, or null when it is not a module code. */
export function moduleCodeType(raw: string): string | null {
  const code = normalizeSerial(raw);
  return MODULE_SN.exec(code)?.[1] ?? MODULE_ARCHIVE_NO.exec(code)?.[1] ?? null;
}

/** The type letter of a module SN — the SN itself, not a module archive number. */
export function moduleSnType(raw: string): string | null {
  return MODULE_SN.exec(normalizeSerial(raw))?.[1] ?? null;
}

export function isRobotSn(raw: string): boolean {
  const code = normalizeSerial(raw);
  return code.length === 18 && ROBOT_SN.test(code);
}

/** Why a module cannot be fitted into a slot (§7 rules 3–4); null when it can. */
export type FitProblem =
  | { reason: "not_active" }
  | { reason: "type_mismatch"; actual: string; expected: string }
  | { reason: "product_type_mismatch" }
  | { reason: "bom_mismatch"; theirs: string | null }
  | { reason: "installed"; where: string }
  | { reason: "incomplete"; empty: string[] };

/**
 * The slot a module is checked against. At filing, `bomId`: the module must be built to the
 * same BOM version as the archive being filed. In a change, `productTypeId` instead — a
 * module of another BOM version may go in (rule 5), one of another machine may not — and
 * `leaving`, the modules the same change takes out of other slots, which are free to move.
 */
export interface FitSlot {
  moduleType: string;
  bomId?: string;
  productTypeId?: string;
  leaving?: ReadonlySet<string>;
}

export function fitProblem(module: ModuleLookup, slot: FitSlot): FitProblem | null {
  if (module.status !== "ACTIVE") return { reason: "not_active" };
  if (module.module_type !== slot.moduleType) {
    return { reason: "type_mismatch", actual: module.module_type, expected: slot.moduleType };
  }
  if (slot.productTypeId !== undefined && module.product_type_id !== slot.productTypeId) {
    return { reason: "product_type_mismatch" };
  }
  if (slot.bomId !== undefined && module.source_bom_id !== slot.bomId) {
    return { reason: "bom_mismatch", theirs: module.bom_version };
  }
  if (module.installed_on.length > 0 && !slot.leaving?.has(module.archive_id)) {
    return {
      reason: "installed",
      where: module.installed_on.map((link) => link.archive_no).join(" · "),
    };
  }
  if (module.empty_slots.length > 0) return { reason: "incomplete", empty: module.empty_slots };
  return null;
}
